import React, { useState } from "react";

interface AuditFinding {
  id: string;
  description: string;
  severity: "Critical" | "Major" | "Minor";
  evidence: string;
  actionPlan: string;
  assignee: string;
  dueDate: string;
  completedDate: string;
  completedBy: string;
  verification: string;
  verifiedDate: string;
}

interface Audit {
  id: string;
  type: string;
  date: string;
  time: string;
  auditor: string;
  area: string;
  score: number;
  maxScore: number;
  findings: AuditFinding[];
  overallNotes: string;
}

const KEY = "deep_audits";
const AUDIT_TYPES = [
  { name: "Kitchen Safety - Morning", area: "Kitchen", frequency: "Daily AM", questions: ["Kitchen supervised at all times", "Boiling water/food secured", "Sharp objects locked away", "Floor clean and dry", "Food temperatures checked", "Staff in proper PPE"] },
  { name: "Kitchen Safety - Evening", area: "Kitchen", frequency: "Daily PM", questions: ["Kitchen supervised at all times", "Boiling water/food secured", "Sharp objects locked away", "Floor clean and dry", "Food temperatures checked", "Waste disposed properly"] },
  { name: "Medication Round Audit", area: "Medication", frequency: "Weekly", questions: ["All staff competency-checked", "CD witnessing completed", "MAR entries accurate", "Refused doses documented", "Missed doses investigated", "Medication storage secure"] },
  { name: "Environment Walk", area: "Environment", frequency: "Daily", questions: ["Fire exits clear", "Call bells working", "Lighting adequate", "Trip hazards removed", "Equipment safe", "Signage visible"] },
  { name: "Handover Quality", area: "Clinical", frequency: "Daily", questions: ["All residents discussed", "Risks communicated", "Changes documented", "Tasks assigned", "Family contact noted", "Incidents reported"] },
  { name: "End-of-Life Care", area: "Clinical", frequency: "Monthly", questions: ["Staff EoL training current", "Care plans reflect wishes", "Preferred place recorded", "DNACPR in place", "Comfort measures documented", "Family support offered"] },
  { name: "Admissions Process", area: "Admissions", frequency: "Per admission", questions: ["Pre-admission risk assessment complete", "Care needs assessed", "Funding confirmed", "GP/pharmacy registered", "Room ready", "First-night plan in place"] },
];

function load(): Audit[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
}

function loadUsers(): any[] {
  try {
    return JSON.parse(localStorage.getItem("rc_staff") || "[]");
  } catch {
    return [];
  }
}

const today = () => new Date().toISOString().slice(0, 10);
const nowTime = () => new Date().toTimeString().slice(0, 5);
const daysTo = (d: string) => Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);

export default function DeepAudit({ user }: { user?: any }) {
  const [audits, setAudits] = useState<Audit[]>(load);
  const users = loadUsers();
  const [type, setType] = useState(AUDIT_TYPES[0].name);
  const [answers, setAnswers] = useState<Record<string, "Pass" | "Fail" | "N/A">>({});
  const [findings, setFindings] = useState<AuditFinding[]>([]);
  const [notes, setNotes] = useState("");
  const [message, setMessage] = useState("");
  const [tab, setTab] = useState<"conduct" | "history" | "recurring" | "overdue">("conduct");

  const save = (next: Audit[]) => {
    setAudits(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  };

  const template = AUDIT_TYPES.find(t => t.name === type)!;

  const toggleAnswer = (q: string, val: "Pass" | "Fail" | "N/A") => {
    setAnswers(a => ({ ...a, [q]: val }));
    if (val === "Fail") {
      if (!findings.some(f => f.id === q)) {
        setFindings(fs => [...fs, { id: q, description: q, severity: "Minor", evidence: "", actionPlan: "", assignee: "", dueDate: "", completedDate: "", completedBy: "", verification: "", verifiedDate: "" }]);
      }
    } else {
      setFindings(fs => fs.filter(f => f.id !== q));
    }
  };

  const updateFinding = (id: string, patch: Partial<AuditFinding>) => {
    setFindings(fs => fs.map(f => (f.id === id ? { ...f, ...patch } : f)));
  };

  const completeAudit = () => {
    const passed = Object.values(answers).filter(v => v === "Pass").length;
    const total = template.questions.length;
    const score = Math.round((passed / total) * 100);
    const rec: Audit = {
      id: `aud-${Date.now()}`,
      type,
      date: today(),
      time: nowTime(),
      auditor: user && user.name ? user.name : "",
      area: template.area,
      score,
      maxScore: 100,
      findings,
      overallNotes: notes,
    };
    save([rec, ...audits]);
    setAnswers({});
    setFindings([]);
    setNotes("");
    setMessage(`Audit completed: ${score}% | ${findings.length} finding(s) with action plans.`);
  };

  const completeFinding = (auditId: string, findingId: string) => {
    save(audits.map(a => (a.id === auditId ? { ...a, findings: a.findings.map(f => (f.id === findingId ? { ...f, completedDate: today(), completedBy: user && user.name ? user.name : "" } : f)) } : a)));
  };

  const verifyFinding = (auditId: string, findingId: string) => {
    save(audits.map(a => (a.id === auditId ? { ...a, findings: a.findings.map(f => (f.id === findingId ? { ...f, verifiedDate: today(), verification: `Verified by ${user && user.name ? user.name : "manager"}` } : f)) } : a)));
  };

  const allFindings = audits.flatMap(a => a.findings.map(f => ({ ...f, auditType: a.type, auditDate: a.date })));
  const overdueFindings = allFindings.filter(f => f.dueDate && daysTo(f.dueDate) < 0 && !f.completedDate);
  const openFindings = allFindings.filter(f => !f.completedDate);

  const recurringIssues = (() => {
    const counts: Record<string, number> = {};
    allFindings.forEach(f => { counts[f.description] = (counts[f.description] || 0) + 1; });
    return Object.entries(counts).filter(([, c]) => c >= 2).sort((a, b) => b[1] - a[1]).map(([desc, count]) => ({ description: desc, count }));
  })();

  const lastAuditsByType = AUDIT_TYPES.map(t => {
    const list = audits.filter(a => a.type === t.name).slice(0, 3);
    return { type: t.name, recent: list };
  });

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Deep Audit Engine</h2>
        <p className="text-sm text-slate-600">Every audit with evidence, findings, action plans, due dates and recurring error detection. Zero holes, fully evidenced.</p>
      </div>

      {overdueFindings.length ? (
        <div className="rounded-lg bg-red-50 p-3 text-sm font-semibold text-red-700">OVERDUE: {overdueFindings.length} action plan(s) past due date. Escalate immediately.</div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {(["conduct", "history", "recurring", "overdue"] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} className={tab === t ? "rounded-lg bg-blue-900 px-3 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-3 py-2 text-sm"}>{t === "conduct" ? "Conduct Audit" : t === "history" ? "Audit History" : t === "recurring" ? "Recurring Issues" : "Overdue Actions"}</button>
        ))}
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      {tab === "conduct" ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <label className="mb-2 block text-sm font-medium text-slate-700">Audit Type</label>
            <select value={type} onChange={e => { setType(e.target.value); setAnswers({}); setFindings([]); }} className="w-full max-w-md rounded-lg border border-slate-300 px-2 py-1 text-sm">
              {AUDIT_TYPES.map(t => <option key={t.name} value={t.name}>{t.name} ({t.frequency})</option>)}
            </select>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 font-semibold text-slate-900">{template.name} - {template.area}</h3>
            <div className="space-y-2">
              {template.questions.map(q => (
                <div key={q} className="rounded-lg border border-slate-100 p-3">
                  <div className="text-sm text-slate-800">{q}</div>
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => toggleAnswer(q, "Pass")} className={answers[q] === "Pass" ? "rounded-lg bg-green-700 px-3 py-1 text-sm text-white" : "rounded-lg border border-slate-300 px-3 py-1 text-sm"}>Pass</button>
                    <button onClick={() => toggleAnswer(q, "Fail")} className={answers[q] === "Fail" ? "rounded-lg bg-red-700 px-3 py-1 text-sm text-white" : "rounded-lg border border-slate-300 px-3 py-1 text-sm"}>Fail</button>
                    <button onClick={() => toggleAnswer(q, "N/A")} className={answers[q] === "N/A" ? "rounded-lg bg-slate-700 px-3 py-1 text-sm text-white" : "rounded-lg border border-slate-300 px-3 py-1 text-sm"}>N/A</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {findings.length ? (
            <div className="rounded-xl border-2 border-red-300 bg-red-50 p-4">
              <h3 className="mb-3 font-semibold text-red-900">{findings.length} Finding(s) - Action Plans Required</h3>
              <div className="space-y-3">
                {findings.map(f => (
                  <div key={f.id} className="rounded-lg border border-red-200 bg-white p-3">
                    <div className="text-sm font-medium text-slate-900">{f.description}</div>
                    <div className="mt-2 grid gap-2 md:grid-cols-3">
                      <select value={f.severity} onChange={e => updateFinding(f.id, { severity: e.target.value as any })} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
                        <option>Minor</option><option>Major</option><option>Critical</option>
                      </select>
                      <input value={f.assignee} onChange={e => updateFinding(f.id, { assignee: e.target.value })} placeholder="Assign to" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                      <input type="date" value={f.dueDate} onChange={e => updateFinding(f.id, { dueDate: e.target.value })} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                      <textarea value={f.evidence} onChange={e => updateFinding(f.id, { evidence: e.target.value })} rows={2} placeholder="Evidence (what you saw, photo ref...)" className="rounded-lg border border-slate-300 px-2 py-1 text-sm md:col-span-3" />
                      <textarea value={f.actionPlan} onChange={e => updateFinding(f.id, { actionPlan: e.target.value })} rows={2} placeholder="Action plan (what will be done, by when)" className="rounded-lg border border-slate-300 px-2 py-1 text-sm md:col-span-3" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <label className="mb-1 block text-xs font-medium text-slate-600">Overall notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          </div>

          <button onClick={completeAudit} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Complete audit</button>
        </div>
      ) : null}

      {tab === "history" ? (
        <div className="space-y-3">
          {audits.map(a => (
            <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-medium text-slate-900">{a.type} <span className="text-xs text-slate-500">{a.date} {a.time} | {a.auditor}</span></div>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${a.score >= 90 ? "bg-green-100 text-green-700" : a.score >= 70 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{a.score}%</span>
              </div>
              {a.findings.length ? (
                <div className="mt-2 space-y-1">
                  {a.findings.map(f => (
                    <div key={f.id} className={`rounded-lg border p-2 text-xs ${f.completedDate ? (f.verifiedDate ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50") : "border-red-200 bg-red-50"}`}>
                      <div className="font-semibold">{f.description} - {f.severity}</div>
                      <div className="mt-1">{f.actionPlan} | Due {f.dueDate} | Assignee {f.assignee}</div>
                      <div className="flex gap-2 mt-1">
                        {!f.completedDate ? <button onClick={() => completeFinding(a.id, f.id)} className="text-blue-700 underline">Mark completed</button> : <span className="text-green-700">Completed {f.completedDate} by {f.completedBy}</span>}
                        {f.completedDate && !f.verifiedDate ? <button onClick={() => verifyFinding(a.id, f.id)} className="text-green-700 underline">Verify</button> : null}
                        {f.verifiedDate ? <span className="text-green-700">Verified {f.verifiedDate}</span> : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : <div className="mt-1 text-xs text-slate-600">No findings</div>}
            </div>
          ))}
          {audits.length === 0 ? <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No audits yet.</div> : null}
        </div>
      ) : null}

      {tab === "recurring" ? (
        <div className="space-y-2">
          {recurringIssues.map(r => (
            <div key={r.description} className="rounded-xl border-2 border-red-300 bg-red-50 p-3">
              <div className="text-sm font-semibold text-red-900">{r.description} - occurred {r.count} times</div>
              <div className="mt-1 text-xs text-red-700">RECURRING ISSUE: Systemic fix required, not just individual action.</div>
            </div>
          ))}
          {recurringIssues.length === 0 ? <div className="rounded-lg bg-green-50 p-4 text-sm text-green-700">No recurring issues detected - excellent.</div> : null}
        </div>
      ) : null}

      {tab === "overdue" ? (
        <div className="space-y-2">
          {overdueFindings.map(f => (
            <div key={`${f.auditDate}-${f.id}`} className="rounded-xl border-2 border-red-300 bg-red-50 p-3">
              <div className="text-sm font-semibold text-red-900">{f.description}</div>
              <div className="mt-1 text-xs text-red-700">From {f.auditType} on {f.auditDate} | Due {f.dueDate} ({Math.abs(daysTo(f.dueDate))} days overdue) | {f.assignee}</div>
              <div className="mt-1 text-xs text-red-700">Action: {f.actionPlan}</div>
            </div>
          ))}
          {overdueFindings.length === 0 ? <div className="rounded-lg bg-green-50 p-4 text-sm text-green-700">No overdue actions - all on track.</div> : null}
        </div>
      ) : null}
    </div>
  );
}